import React from 'react';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import { Form, PrimaryButton, FieldTextInput, NamedLink } from '../../../components';

import css from './PasswordlessLoginForm.module.css';

const PasswordlessLoginFormComponent = props => (
  <FinalForm
    {...props}
    render={fieldRenderProps => {
      const {
        rootClassName,
        className,
        formId,
        handleSubmit,
        inProgress,
        intl,
        invalid,
        isLogin,
        sent,
        sentEmail,
        onTryAgain,
        onTogglePassword,
        values,
        errors,
      } = fieldRenderProps;

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;
      const submitDisabled = invalid || submitInProgress;

      if (sent) {
        return (
          <div className={classes}>
            <p className={css.sentNotice}>
              <FormattedMessage
                id="PasswordlessLoginForm.linkSent"
                values={{ email: <span className={css.sentEmail}>{sentEmail}</span> }}
              />
            </p>
            <button type="button" className={css.tryAgainLink} onClick={onTryAgain}>
              <FormattedMessage id="PasswordlessLoginForm.tryAgain" />
            </button>
          </div>
        );
      }

      const emailLabel = intl.formatMessage({ id: 'PasswordlessLoginForm.emailLabel' });
      const emailPlaceholder = intl.formatMessage({ id: 'PasswordlessLoginForm.emailPlaceholder' });
      const emailRequired = validators.required(
        intl.formatMessage({ id: 'PasswordlessLoginForm.emailRequired' })
      );
      const emailValid = validators.emailFormatValid(
        intl.formatMessage({ id: 'PasswordlessLoginForm.emailInvalid' })
      );

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <FieldTextInput
            type="email"
            id={formId ? `${formId}.email` : 'email'}
            name="email"
            autoComplete="email"
            label={emailLabel}
            placeholder={emailPlaceholder}
            validate={validators.composeValidators(emailRequired, emailValid)}
          />
          <div className={css.bottomWrapper}>
            <PrimaryButton type="submit" inProgress={submitInProgress} disabled={submitDisabled}>
              <FormattedMessage
                id={isLogin ? 'PasswordlessLoginForm.sendLoginLink' : 'PasswordlessLoginForm.sendSignupLink'}
              />
            </PrimaryButton>
            {onTogglePassword ? (
              <button type="button" className={css.togglePasswordLink} onClick={onTogglePassword}>
                <FormattedMessage id="PasswordlessLoginForm.usePasswordInstead" />
              </button>
            ) : null}
            {isLogin ? (
              <NamedLink
                className={css.setPasswordLink}
                name="PasswordRecoveryPage"
                to={{
                  search:
                    values?.email && !errors?.email
                      ? `email=${encodeURIComponent(values.email)}`
                      : '',
                }}
              >
                <FormattedMessage id="PasswordlessLoginForm.setPasswordInstead" />
              </NamedLink>
            ) : null}
          </div>
        </Form>
      );
    }}
  />
);

/**
 * A component that renders the passwordless (magic-link) login/signup
 * form -- collects only an email address, the account is matched or
 * created server-side once the emailed link is clicked (see
 * server/api/auth/passwordless.js).
 *
 * @component
 * @param {Object} props
 * @param {string} props.rootClassName
 * @param {string} props.className
 * @param {string} props.formId
 * @param {boolean} props.inProgress
 * @param {boolean} props.isLogin
 * @param {boolean} props.sent - whether a link was just successfully requested
 * @param {string} props.sentEmail - the email the link was sent to
 * @param {Function} props.onTryAgain - resets back to the email input
 * @param {Function} [props.onTogglePassword] - reveals the legacy password form, omit to hide the toggle
 * @returns {JSX.Element}
 */
const PasswordlessLoginForm = props => {
  const intl = useIntl();
  return <PasswordlessLoginFormComponent {...props} intl={intl} />;
};

export default PasswordlessLoginForm;
